import { useCallback, useEffect, useState } from "react";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import {
  isSectionOpen,
  readSectionState,
  writeSectionState,
  type SectionKey,
} from "@/lib/section-collapse-store";

/**
 * VIB-2100: åpen/lukket-tilstand for en seksjon, husket per bruker og enhet.
 *
 * Drop-in for `useState(defaultOpen)` i en seksjonskomponent.
 *
 * `key: null` gir vanlig, flyktig tilstand. Det finnes for komponenter som
 * brukes flere steder: `MyTasksList` står både på Mine oppgaver og på et
 * prosjekts oversikt, og de to skal ikke dele én husket tilstand. Kallestedet
 * bestemmer hvilken forekomst som er «seksjonen» — ikke komponenten selv.
 *
 * Bruker `useImpersonation()` og ikke `useAuth()`, som ellers i appen: ser en
 * admin på Mine oppgaver gjennom en annen brukers øyne, skal det være den
 * brukerens seksjonsoppsett som gjelder for visningen.
 */
export function usePersistedCollapse(
  key: SectionKey | null,
  defaultOpen: boolean,
): [boolean, (open: boolean) => void] {
  const { activeUser } = useImpersonation();
  const userId = activeUser?.id ?? null;

  // Lazy init: leser vi i en useEffect i stedet, rekker seksjonen å rendre
  // åpen først og smelle igjen etterpå. Brukeren skal ikke se den blafre.
  const [open, setOpen] = useState(() =>
    key ? isSectionOpen(readSectionState(userId), key, defaultOpen) : defaultOpen,
  );

  // Brukeren er ikke alltid kjent ved første render (auth laster, eller admin
  // bytter impersonering mens siden står åpen). Synk når den endrer seg.
  useEffect(() => {
    if (!key) return;
    setOpen(isSectionOpen(readSectionState(userId), key, defaultOpen));
  }, [userId, key, defaultOpen]);

  const set = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (key) writeSectionState(userId, key, next);
    },
    [userId, key],
  );

  return [open, set];
}
